/**
 * One Today card (13-09; the judgement verbs 13-10), built to design/mock-13 (D-13-02-1).
 *
 * Shows the kind chip, the title, the ref and source, the due date (overdue in a gentle amber), the
 * release note (Oversight's words to the owner, 09.1 §8b) and only the verbs `canDoVerb` allows:
 * a disallowed verb is hidden, not shown disabled. A verification card that would be refused says
 * why (ISS-188).
 *
 *   - Decision rows go to `DecisionCard`: the ordinary inset (recommendation first, one-tap Agree)
 *     or, when `isRegulatoryDecision` says so, the regulatory verbs (Decide opens the panel,
 *     Oversight only; everyone else reads "Waiting on Oversight").
 *   - Done rows in Oversight's lens get `VerifyActions`: Verify (undo), Sampled OK, Reject (E3).
 *   - Accept: the card stays, now Accepted (D-13-02-1).
 *   - Done on an `evidence` row: an inline proof field, focused, Enter to submit (one extra
 *     keystroke, not a modal). A pasted SharePoint link over 255 characters is kept (Evidence is a
 *     Note column) with a tip that a short ref is easier to verify.
 *   - Not mine: an inline note (optional), then the row goes back to Oversight.
 *   - Reassign (Oversight, ISS-186): a new owner role and a required note.
 *   - Retire… (Oversight, ISS-212): a quiet overflow on released, still-open cards, opening a drawer
 *     with a required note (at least 10 characters) and a charcoal confirm. It commits at once with
 *     no undo toast, like the regulatory decide.
 */

import { memo, useEffect, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { Button, Menu, MenuItem, MenuTrigger, Popover } from 'react-aria-components';
import { OWNER_ROLES } from '../../config/actionRegister';
import { canDoVerb } from '../../lib/actionAuthority';
import { dueLabel, shortDay } from '../../lib/briefing';
import { melbourneDay } from '../../lib/actionVerbs';
import type { VerbInput } from '../../lib/actionVerbs';
import { RETIRE_NOTE_MIN, ROLE_LABEL, cardRefusalText, isLongSharingLink, retireNoteReady, runRefusalText } from '../../lib/todayQueue';
import type { QueueCard } from '../../lib/todayQueue';
import type { AuthorityContext } from '../../lib/actionAuthority';
import type { CardNote, RunCopy, RunOptions, RunResult } from '../../hooks/useActionQueue';
import type { ActionRow, ActionVerb, Actor, OwnerRole } from '../../types/actionRegister';
import { DecisionInset, RegulatoryVerbs } from './DecisionCard';
import VerifyActions from './VerifyActions';
import styles from './today.module.css';

const cx = (...names: Array<string | false | null | undefined>) => names.filter(Boolean).join(' ');
/** About three lines of the card's width; longer details get a "Show all details" toggle. */
const ABOUT_CLAMP_CHARS = 200;

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------

const Check = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M5 12.5l4.2 4.2L19 7" />
  </svg>
);

const OkGlyph = () => (
  <svg className={styles.okglyph} viewBox="0 0 24 24" aria-hidden="true">
    <path d="M5 12.5l4.2 4.2L19 7" />
  </svg>
);

const DecisionIcon = () => (
  <svg className={styles.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 3v18" />
    <path d="M12 8h5M12 12h6M12 16h5" opacity=".55" />
  </svg>
);

const RegIcon = () => (
  <svg className={styles.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 4v16M7 20h10M5 7h14" />
    <path d="M5 7l-3 6a3 3 0 0 0 6 0z" />
    <path d="M19 7l-3 6a3 3 0 0 0 6 0z" />
  </svg>
);

const VerifyIcon = () => (
  <svg className={styles.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3l7 3v5c0 4.5-3 8.3-7 10-4-1.7-7-5.5-7-10V6z" />
    <path d="M9 12l2 2 4-4" opacity=".55" />
  </svg>
);

const HandIcon = () => (
  <svg className={styles.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 14L4 9l5-5" />
    <path d="M4 9h10.5a5.5 5.5 0 0 1 0 11H11" />
  </svg>
);

// ---------------------------------------------------------------------------
// The card
// ---------------------------------------------------------------------------

const MoreGlyph = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="5.5" cy="12" r="1.6" />
    <circle cx="12" cy="12" r="1.6" />
    <circle cx="18.5" cy="12" r="1.6" />
  </svg>
);

type Drawer = 'proof' | 'notMine' | 'reassign' | 'retire' | null;

export interface CardHandlers {
  run: (verb: ActionVerb, row: ActionRow, input: VerbInput, copy: RunCopy, ctx?: AuthorityContext, opts?: RunOptions) => RunResult;
  /** E3 from the card: a fresh audit read, then reject (13-10). */
  reject: (row: ActionRow, reason: string) => Promise<RunResult>;
  /** Opens the regulatory panel: to decide (Oversight) or read-only. */
  openRegulatory: (row: ActionRow, readOnly: boolean) => void;
  retry: (id: number) => void;
  discard: (id: number) => void;
}

export interface ActionCardProps {
  card: QueueCard;
  actor: Actor;
  /** Melbourne "today" for the due and done labels. */
  now: number;
  ticked: boolean;
  note?: CardNote;
  handlers: CardHandlers;
}

function chip(card: QueueCard): [string, string | undefined] {
  const { row, kind } = card;
  switch (kind) {
    case 'action':
      return row.status === 'Proposed' ? [`New ${row.kind === 'Approval' ? 'approval' : 'action'}`, styles.kNew] : [row.kind, undefined];
    case 'evidence':
      return ['Needs proof', styles.kProof];
    case 'decision':
      return ['Decision', undefined];
    case 'regulatory':
      return ['Regulatory call', styles.kReg];
    case 'verify':
      return ['To verify', undefined];
    case 'handedBack':
      return ['Handed back to you to reassign', styles.kNew];
  }
}

function doneWhen(row: ActionRow, now: number): string {
  if (!row.completedAt) return 'Done';
  const day = melbourneDay(row.completedAt);
  const today = melbourneDay(new Date(now));
  if (day === today) return 'Done today';
  const y = melbourneDay(new Date(now - 86_400_000));
  return day === y ? 'Done yesterday' : `Done ${shortDay(day)}`;
}

function sourceText(row: ActionRow): string {
  if (!row.sourceRef) return row.source;
  return row.source === 'Other' ? row.sourceRef : `${row.source} ${row.sourceRef}`;
}

function ActionCardImpl({ card, actor, now, ticked, note, handlers }: ActionCardProps) {
  const { row, kind } = card;
  const [drawer, setDrawer] = useState<Drawer>(null);
  // 13.1-14 (ISS-241): the action's details, three lines until opened, so the owner knows what is being asked.
  const [aboutOpen, setAboutOpen] = useState(false);
  const about = (row.description ?? '').trim();
  const [proof, setProof] = useState('');
  const [why, setWhy] = useState('');
  const [role, setRole] = useState<OwnerRole | ''>('');
  const [reassignNote, setReassignNote] = useState('');
  const [retireNote, setRetireNote] = useState('');
  const [refusal, setRefusal] = useState<string | null>(null);
  const firstField = useRef<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (!drawer) return;
    // After the overflow menu closes and hands focus back, so the field keeps it (ISS-212).
    const t = window.setTimeout(() => firstField.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [drawer]);

  const busy = ticked || note?.tone === 'failed';
  const can = (verb: ActionVerb) => canDoVerb(verb, row, actor).ok;

  const act = (verb: ActionVerb, input: VerbInput, copy: RunCopy, ctx?: AuthorityContext, opts?: RunOptions) => {
    const result = handlers.run(verb, row, input, copy, ctx, opts);
    if (result.ok) {
      setDrawer(null);
      setRefusal(null);
    } else if (result.reason !== 'busy') {
      setRefusal(runRefusalText(result.reason));
    }
  };

  const accept = () =>
    act('accept', {}, { title: `Accepted: ${row.title}`, description: "It's yours now. Done when you're ready." });
  const done = () => act('done', {}, { title: `Done: ${row.title}` });
  const doneWithProof = () => {
    if (!proof.trim()) return;
    act('done', { evidence: proof.trim() }, { title: `Done, with proof: ${row.title}` });
  };
  const notMine = () => {
    const reason = why.trim();
    act('notMine', reason ? { note: reason } : {}, {
      title: `Handed back to Oversight: ${row.title}`,
      description: reason ? `“${reason}”` : 'Oversight reassigns it.',
    });
  };
  const reassign = () => {
    if (!role || !reassignNote.trim()) return;
    act(
      'reassign',
      { ownerRole: role, note: reassignNote.trim() },
      { title: `Reassigned to ${ROLE_LABEL[role]}: ${row.title}`, description: 'It goes to their queue with your note.' },
    );
  };

  /**
   * ISS-212: retire a released, still-open row (Oversight). Deliberate, like the regulatory decide:
   * the drawer's charcoal button is the confirm, so it commits at once with no undo toast.
   */
  const retire = () => {
    if (!card.retire || !retireNoteReady(retireNote)) return;
    act(
      'retire',
      { note: retireNote.trim() },
      { title: `Retired: ${row.title}`, description: 'It has left every queue. Your note is on the row and in its trail.' },
      undefined,
      { immediate: true },
    );
  };

  /** An ordinary decision (never regulatory: the card routed it here only when the classifier said so). */
  const decideOrdinary = (input: VerbInput, copy: RunCopy): RunResult => {
    const result = handlers.run('decide', row, input, copy);
    if (result.ok) setRefusal(null);
    else if (result.reason !== 'busy') setRefusal(runRefusalText(result.reason));
    return result;
  };

  const onFieldKey = (submit: () => void) => (e: KeyboardEvent<HTMLInputElement | HTMLSelectElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      submit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setDrawer(null);
    }
  };

  const due = kind === 'verify' ? { text: doneWhen(row, now), overdue: false } : dueLabel(row, new Date(now));
  const [chipText, chipClass] = chip(card);
  const isNew = row.status === 'Proposed';

  // ---- the glyph ----
  let glyph: ReactNode;
  if (kind === 'action' && isNew && can('accept')) {
    glyph = (
      <button type="button" className={cx(styles.glyph, styles.tick)} onClick={accept} disabled={busy} aria-label={`Accept: ${row.title}`}>
        <Check />
      </button>
    );
  } else if (kind === 'action' && !isNew && can('done')) {
    glyph = (
      <button type="button" className={cx(styles.glyph, styles.tick)} onClick={done} disabled={busy} aria-label={`Done: ${row.title}`}>
        <Check />
      </button>
    );
  } else if (kind === 'evidence' && can('done')) {
    glyph = (
      <button
        type="button"
        className={cx(styles.glyph, styles.tick, styles.tickSq)}
        onClick={() => setDrawer('proof')}
        disabled={busy}
        aria-label={`Done, with proof: ${row.title}`}
      >
        <Check />
      </button>
    );
  } else {
    const icon =
      kind === 'regulatory' ? <RegIcon /> : kind === 'decision' ? <DecisionIcon /> : kind === 'handedBack' ? <HandIcon /> : <VerifyIcon />;
    glyph = (
      <span className={cx(styles.glyph, styles.static, kind === 'regulatory' && styles.reg)} aria-hidden="true">
        {icon}
        <OkGlyph />
      </span>
    );
  }

  // ---- the verbs ----
  let verbs: ReactNode = null;
  const notMineButton = can('notMine') ? (
    <button type="button" className={cx(styles.btn, styles.quiet)} onClick={() => setDrawer('notMine')} disabled={busy} aria-expanded={drawer === 'notMine'}>
      Not mine
    </button>
  ) : null;

  if (note?.tone === 'failed') {
    verbs = null; // the note carries Retry / Don't save
  } else if (kind === 'action') {
    const primary = isNew ? (can('accept') ? 'accept' : null) : can('done') ? 'done' : null;
    if (primary || notMineButton) {
      verbs = (
        <div className={styles.verbs}>
          {primary === 'accept' && (
            <button type="button" data-primary className={cx(styles.btn, styles.go)} onClick={accept} disabled={busy}>
              Accept
            </button>
          )}
          {primary === 'done' && (
            <button type="button" data-primary className={cx(styles.btn, styles.go)} onClick={done} disabled={busy}>
              Done
            </button>
          )}
          {notMineButton}
        </div>
      );
    }
  } else if (kind === 'evidence') {
    if (drawer !== 'proof') {
      verbs = (
        <div className={styles.verbs}>
          {can('done') && (
            <button type="button" data-primary className={cx(styles.btn, styles.go)} onClick={() => setDrawer('proof')} disabled={busy} aria-expanded={false}>
              Done
            </button>
          )}
          {notMineButton}
        </div>
      );
    }
  } else if (kind === 'regulatory') {
    verbs = <RegulatoryVerbs row={row} actor={actor} busy={busy} onOpen={handlers.openRegulatory} />;
  } else if (kind === 'decision') {
    verbs = <DecisionInset row={row} actor={actor} busy={busy} decide={(input, copy) => decideOrdinary(input, copy)} />;
  } else if (kind === 'verify') {
    verbs = (
      <VerifyActions
        card={card}
        busy={busy}
        verify={(input, copy, ctx) => act('verify', input, copy, ctx)}
        reject={handlers.reject}
        onRefused={(reason) => setRefusal(runRefusalText(reason))}
      />
    );
  } else if (kind === 'handedBack' && can('reassign') && drawer !== 'reassign') {
    verbs = (
      <div className={styles.verbs}>
        <button type="button" data-primary className={cx(styles.btn, styles.line)} onClick={() => setDrawer('reassign')} disabled={busy} aria-expanded={false}>
          Reassign
        </button>
        <span className={styles.sub}>Its owner handed it back. Choose who should have it.</span>
      </div>
    );
  }

  const id = `c${row.id}`;
  const longLink = isLongSharingLink(proof);

  return (
    <div
      className={cx(styles.row)}
      aria-busy={ticked || undefined}
    >
      {glyph}
      <div className={styles.body}>
        <h3 className={styles.title}>{row.title}</h3>
        {about && <p className={cx(styles.about, !aboutOpen && styles.aboutShut)}>{about}</p>}
        {about.length > ABOUT_CLAMP_CHARS && (
          <button
            type="button"
            className={styles.aboutToggle}
            aria-expanded={aboutOpen}
            onClick={() => setAboutOpen((o) => !o)}
          >
            {aboutOpen ? 'Show less' : 'Show all details'}
          </button>
        )}
        <div className={styles.meta}>
          <span className={cx(styles.kind, chipClass)}>{chipText}</span>
          <span className={styles.ref}>{row.reference}</span>
          <span>from {sourceText(row)}</span>
        </div>
      </div>
      <div className={styles.side}>
        <div className={cx(styles.due, due?.overdue && styles.late)}>{due?.text ?? ''}</div>
        {card.retire && !busy && drawer !== 'retire' && (
          // ISS-212: a quiet overflow, never a primary button. Oversight only (the view model says so).
          <MenuTrigger>
            <Button className={styles.more} aria-label={`More for ${row.title}`}>
              <MoreGlyph />
            </Button>
            <Popover className={styles.popover} placement="bottom end" offset={6}>
              <Menu className={styles.menu} aria-label={`More for ${row.reference}`} onAction={(key) => key === 'retire' && setDrawer('retire')}>
                <MenuItem id="retire" className={styles.menuItem}>
                  Retire…
                </MenuItem>
              </Menu>
            </Popover>
          </MenuTrigger>
        )}
      </div>

      {row.releaseNote && (kind === 'action' || kind === 'evidence' || kind === 'decision') && (
        <div className={styles.quote}>
          <b>Note from Oversight</b>
          {row.releaseNote}
        </div>
      )}

      {kind === 'verify' && (
        <div className={styles.quote}>
          <b>Proof</b>
          {row.evidence?.trim() || 'No proof recorded.'}
        </div>
      )}

      {verbs}

      {kind === 'verify' && card.refusal && (
        <p className={cx(styles.note, card.refusal === 'checking' && styles.noteCalm)} role="status">
          {cardRefusalText(card.refusal)}
        </p>
      )}

      {drawer === 'proof' && (
        <div className={styles.drawer}>
          <label htmlFor={`${id}-proof`}>Where&rsquo;s the proof?</label>
          <div className={styles.field}>
            <input
              ref={(el) => {
                firstField.current = el;
              }}
              id={`${id}-proof`}
              className={styles.input}
              value={proof}
              onChange={(e) => setProof(e.target.value)}
              onKeyDown={onFieldKey(doneWithProof)}
              placeholder="A ref or short pointer"
              autoComplete="off"
            />
            <button type="button" className={cx(styles.btn, styles.go, styles.sm)} onClick={doneWithProof} disabled={!proof.trim() || busy}>
              Done
            </button>
            <button type="button" className={cx(styles.btn, styles.quiet, styles.sm)} onClick={() => setDrawer(null)}>
              Cancel
            </button>
          </div>
          {longLink ? (
            <p className={styles.tip}>Tip: a short ref is easier to verify. The full link is kept.</p>
          ) : (
            <p className={styles.hint}>This action is checked against its proof, so Done needs a link or a location.</p>
          )}
        </div>
      )}

      {drawer === 'notMine' && (
        <div className={styles.drawer}>
          <label htmlFor={`${id}-why`}>
            Who should have it? <span className={styles.opt}>(optional)</span>
          </label>
          <div className={styles.field}>
            <input
              ref={(el) => {
                firstField.current = el;
              }}
              id={`${id}-why`}
              className={styles.input}
              value={why}
              onChange={(e) => setWhy(e.target.value)}
              onKeyDown={onFieldKey(notMine)}
              placeholder="One line, e.g. this client moved to another Care Manager"
              autoComplete="off"
            />
            <button type="button" className={cx(styles.btn, styles.line, styles.sm)} onClick={notMine} disabled={busy}>
              Hand back
            </button>
            <button type="button" className={cx(styles.btn, styles.quiet, styles.sm)} onClick={() => setDrawer(null)}>
              Cancel
            </button>
          </div>
          <p className={styles.hint}>It goes back to Oversight to reassign.</p>
        </div>
      )}

      {drawer === 'reassign' && (
        <div className={styles.drawer}>
          <div className={styles.field}>
            <div>
              <label htmlFor={`${id}-role`}>Who should own it now?</label>
              <select
                ref={(el) => {
                  firstField.current = el;
                }}
                id={`${id}-role`}
                className={styles.select}
                value={role}
                onChange={(e) => setRole(e.target.value as OwnerRole | '')}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setDrawer(null);
                }}
              >
                <option value="">Choose a role</option>
                {OWNER_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_LABEL[r]}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <label htmlFor={`${id}-rnote`}>A note for them</label>
              <input
                id={`${id}-rnote`}
                className={styles.input}
                style={{ width: '100%' }}
                value={reassignNote}
                onChange={(e) => setReassignNote(e.target.value)}
                onKeyDown={onFieldKey(reassign)}
                placeholder="Why it's theirs, in one line"
                autoComplete="off"
              />
            </div>
          </div>
          <div className={styles.field} style={{ marginTop: 10 }}>
            <button type="button" className={cx(styles.btn, styles.go, styles.sm)} onClick={reassign} disabled={!role || !reassignNote.trim() || busy}>
              Reassign
            </button>
            <button type="button" className={cx(styles.btn, styles.quiet, styles.sm)} onClick={() => setDrawer(null)}>
              Cancel
            </button>
          </div>
          <p className={styles.hint}>It goes back to Proposed in that role&rsquo;s queue, with your note in its trail.</p>
        </div>
      )}

      {drawer === 'retire' && card.retire && (
        <div className={cx(styles.drawer, styles.retireDrawer)}>
          <label htmlFor={`${id}-retire`}>
            Why retire it? <span className={styles.opt}>(required, at least {RETIRE_NOTE_MIN} characters)</span>
          </label>
          <textarea
            ref={(el) => {
              firstField.current = el;
            }}
            id={`${id}-retire`}
            className={cx(styles.input, styles.textarea)}
            value={retireNote}
            onChange={(e) => setRetireNote(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.preventDefault();
                setDrawer(null);
              }
            }}
            placeholder="e.g. Practice row: the walkthrough is finished"
            aria-describedby={`${id}-retire-hint`}
            maxLength={2000}
          />
          <div id={`${id}-retire-hint`}>
            <p className={styles.hint}>
              Retiring closes it for good. It leaves every queue, is never carded, and there&rsquo;s no undo. Your note goes on
              the row and in its trail.
            </p>
            {kind === 'regulatory' && (
              <p className={styles.hint}>This is a regulatory call. Retiring it means nobody makes that call here.</p>
            )}
          </div>
          <div className={styles.field} style={{ marginTop: 10 }}>
            <button
              type="button"
              className={cx(styles.btn, styles.solemn, styles.sm)}
              onClick={retire}
              disabled={!retireNoteReady(retireNote) || busy}
            >
              Retire {row.reference}
            </button>
            <button type="button" className={cx(styles.btn, styles.quiet, styles.sm)} onClick={() => setDrawer(null)}>
              Cancel
            </button>
            {!retireNoteReady(retireNote) && (
              <span className={styles.sub} aria-live="polite">
                {retireNote.trim().length} of {RETIRE_NOTE_MIN}
              </span>
            )}
          </div>
        </div>
      )}

      {note && (
        <p className={styles.note} role="status">
          <span>{note.tone === 'failed' ? 'Couldn’t save.' : note.text}</span>
          {note.tone === 'failed' && (
            <>
              <button type="button" className={cx(styles.btn, styles.line, styles.sm)} onClick={() => handlers.retry(row.id)}>
                Retry
              </button>
              <button type="button" className={cx(styles.btn, styles.quiet, styles.sm)} onClick={() => handlers.discard(row.id)}>
                Don&rsquo;t save
              </button>
            </>
          )}
        </p>
      )}

      {refusal && !note && (
        <p className={styles.note} role="status">
          {refusal}
        </p>
      )}
    </div>
  );
}

export const ActionCard = memo(ActionCardImpl);
export default ActionCard;
